import type { MessengerMessage } from '@/api/client';
import { MESSENGER_OPTIMISTIC_ID_PREFIX } from '@/constants/messengerOptimistic';
import {
  normalizeMessengerMessage,
  parseMessengerFileSizeBytes,
} from '@/utils/messengerMessageNormalize';

/** Оптимистичное сообщение с blob-превью соответствует пришедшему с сервера (тот же файл/текст). */
export function matchesDirectOptimisticWithServer(
  optimistic: MessengerMessage,
  serverMsg: MessengerMessage
): boolean {
  if (!optimistic.id.startsWith(MESSENGER_OPTIMISTIC_ID_PREFIX)) return false;
  if (String(optimistic.sender_id) !== String(serverMsg.sender_id)) return false;
  if (optimistic.body !== serverMsg.body) return false;
  const ou = optimistic.file_url || '';
  const su = serverMsg.file_url || '';
  if (ou === su) return true;
  if (typeof ou === 'string' && ou.startsWith('blob:') && su && !String(su).startsWith('blob:')) {
    return (
      optimistic.file_name === serverMsg.file_name &&
      parseMessengerFileSizeBytes(optimistic.file_size) === parseMessengerFileSizeBytes(serverMsg.file_size)
    );
  }
  return false;
}

export function mergeServerMessagesWithPendingOptimistic(
  server: MessengerMessage[],
  prev: MessengerMessage[]
): MessengerMessage[] {
  const opt = prev.filter((m) => m.id.startsWith(MESSENGER_OPTIMISTIC_ID_PREFIX));
  if (opt.length === 0) return server;
  const extra = opt.filter((o) => !server.some((s) => matchesDirectOptimisticWithServer(o, s)));
  return extra.length === 0 ? server : [...server, ...extra];
}

export function appendIncomingDmMessage(prev: MessengerMessage[], msg: MessengerMessage): MessengerMessage[] {
  if (prev.some((m) => m.id === msg.id)) return prev;
  const optIdx = prev.findIndex((m) => matchesDirectOptimisticWithServer(m, msg));
  if (optIdx >= 0) {
    const next = [...prev];
    const optimistic = next[optIdx]!;
    const stableBubbleKey = optimistic._client?.stableBubbleKey ?? optimistic.id;
    next[optIdx] = normalizeMessengerMessage({
      ...msg,
      image_width: msg.image_width ?? optimistic.image_width ?? null,
      image_height: msg.image_height ?? optimistic.image_height ?? null,
      _client: { ...msg._client, stableBubbleKey },
    });
    return next;
  }
  return [...prev, normalizeMessengerMessage(msg)];
}

export function patchMessageReadAtInPlace(
  prev: MessengerMessage[],
  messageId: string
): MessengerMessage[] | null {
  const idx = prev.findIndex((m) => m.id === messageId);
  if (idx < 0) return null;
  const m = prev[idx];
  if (m.read_at) return null;
  const next = [...prev];
  next[idx] = {
    ...m,
    read_at: new Date().toISOString(),
    delivered_at: m.delivered_at ?? new Date().toISOString(),
  };
  return next;
}

export function patchMessageDeliveredAtInPlace(
  prev: MessengerMessage[],
  messageId: string,
  deliveredAt?: string | null
): MessengerMessage[] | null {
  const idx = prev.findIndex((m) => m.id === messageId);
  if (idx < 0) return null;
  const m = prev[idx];
  if (m.delivered_at || m.read_at) return null;
  const next = [...prev];
  next[idx] = {
    ...m,
    delivered_at: deliveredAt?.trim() || new Date().toISOString(),
  };
  return next;
}
